import { lazy, ComponentType } from 'react';

/**
 * Utility to wrap React.lazy with a retry mechanism.
 * Useful for handling "Failed to fetch dynamically imported module" errors
 * caused by network issues or new deployments with different chunk hashes.
 *
 * @param componentImport - Function that returns the dynamic import promise.
 * @param retriesLeft - Number of retry attempts (default: 2).
 * @param interval - Initial time in ms to wait between retries (default: 1000).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  retriesLeft = 2,
  interval = 1000,
) {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error: unknown) {
      let currentError = error;

      for (let i = 0; i < retriesLeft; i++) {
        try {
          // Wait before retrying (exponential backoff of sorts: 1s, 2s...)
          await new Promise((resolve) => setTimeout(resolve, interval * (i + 1)));
          return await componentImport();
        } catch (retryError: unknown) {
          currentError = retryError;
        }
      }

      // If no retries left, check if it's a chunk load error
      const errorMessage = currentError instanceof Error ? currentError.message : String(currentError);
      const isChunkLoadError = errorMessage.includes('Failed to fetch dynamically imported module');

      if (isChunkLoadError) {
        const now = Date.now();
        const lastReload = sessionStorage.getItem('chunk-load-last-reload');
        const COOLDOWN_MS = 300000; // Evita bucles inmediatos, pero permite reintentos tras 300s

        if (!lastReload || now - parseInt(lastReload, 10) > COOLDOWN_MS) {
          sessionStorage.setItem('chunk-load-last-reload', now.toString());
          window.location.reload();
          // Devuelve un componente nulo mientras la página se recarga
          return { default: (() => null) as unknown as T };
        }
      }

      // If it's not a chunk error or we already reloaded recently, throw it
      throw currentError;
    }
  });
}
